export const SYSTEM_ROLES = {
  GLOBAL_ADMIN:   'global_admin',
  ENTITY_ADMIN:   'entity_admin',
  GMS_ACCOUNTANT: 'gms_accountant',
};

export const SPACE_ROLES = {
  SPACE_ADMIN:       'space_admin',
  EVENT_HOST_3D:     'event_host_3d',
  MARKETING:         'marketing',
  MARKETING_MANAGER: 'marketing_manager',
  EVENT_HOST_2D:     'event_host_2d',
  STAR_GUEST:        'star_guest',
  FINANCE:           'finance',
  DATA_ANALYST:      'data_analyst',
  DEMO_USER:         'demo_user', // DEMO_USER: revert this line
};

// ── Navigation access + intra-page feature rules ─────────────────────────────
//
// Each route entry has:
//   access   → who can enter the page (spaceRoles | systemRoles)
//   features → optional map of feature keys to role rules
//              no entry for a feature = visible to all who can access the page
//
// Pages not listed here are hidden from the sidebar and blocked by ProtectedRoute.
// ─────────────────────────────────────────────────────────────────────────────
export const NAV_ACCESS = {
  '/': {
    access: { spaceRoles: Object.values(SPACE_ROLES), systemRoles: Object.values(SYSTEM_ROLES) },
  },
  '/booth': {
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER] },
    features: {
      'booth-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
    },
  },
  '/events': {
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER, SPACE_ROLES.EVENT_HOST_3D, SPACE_ROLES.EVENT_HOST_2D] },
    features: {
      'event-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.EVENT_HOST_3D, SPACE_ROLES.EVENT_HOST_2D] },
    },
  },
  '/teleport-map': {
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER] },
    features: {
      'teleport-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
    },
  },
  '/items': {
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER] },
    features: {
      'items-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
      // Free Trial tab: same edit split as items-edit — marketing_manager keeps
      // view/approve-only access via the existing approvals-inbox flow.
      'free-trial-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
    },
  },
  '/game-score-board': {
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER] },
    features: {
      'game-score-board-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
    },
  },
  // Game Schedule (the Quiz): space_admin and marketing manage it; marketing_manager only views it and
  // the participants — approving happens in the Approvals screen. No space feature flag (like the scoreboard).
  '/game-schedule': {
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER] },
    features: {
      'game-schedule-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
    },
  },
  '/recognition': {
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER] },
    features: {
      'recognition-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
    },
  },
  '/charities': {
    access: {
      spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER, SPACE_ROLES.FINANCE],
      systemRoles: [SYSTEM_ROLES.GMS_ACCOUNTANT],
    },
    features: {
      'charity-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
    },
  },
  '/advertisers': {
    access: {
      spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER, SPACE_ROLES.FINANCE],
      systemRoles: [SYSTEM_ROLES.GMS_ACCOUNTANT],
    },
    features: {
      'advertiser-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING] },
    },
  },
  // '/hosting' is disabled as a standalone page for now — delivery is configured inline under Event (2D format).
  // '/hosting': {
  //   access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING_MANAGER, SPACE_ROLES.EVENT_HOST_3D, SPACE_ROLES.EVENT_HOST_2D] },
  //   features: {
  //     'hosting-edit': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.EVENT_HOST_3D, SPACE_ROLES.EVENT_HOST_2D] },
  //   },
  // },
  '/settings': {
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN] },
  },

  '/payments': {
    access: {
      spaceRoles: [SPACE_ROLES.FINANCE],
      systemRoles: [SYSTEM_ROLES.GMS_ACCOUNTANT, SYSTEM_ROLES.GLOBAL_ADMIN],
    },
    features: {
      // finance sees gateway config and can edit/toggle
      'gateways-tab':        { spaceRoles: [SPACE_ROLES.FINANCE] },
      'gateway-save':        { spaceRoles: [SPACE_ROLES.FINANCE] },
      'gateway-toggle':      { spaceRoles: [SPACE_ROLES.FINANCE] },
      // gms_accountant and global_admin (system roles) use the global cross-space
      // transactions/deposits endpoints; finance uses the space-scoped endpoint
      // (no feature entry needed — it's the default)
      'transactions-global': { systemRoles: [SYSTEM_ROLES.GMS_ACCOUNTANT, SYSTEM_ROLES.GLOBAL_ADMIN] },
      // Deposits tab: creating a deposit (the wizard) and uploading/replacing its wire
      // transfer document — finance only. gms_accountant and global_admin are
      // view/download only everywhere.
      'wire-doc-upload':     { spaceRoles: [SPACE_ROLES.FINANCE] },
    },
  },

  '/approvals': {
    // DEMO_USER: revert — remove SPACE_ROLES.DEMO_USER from all three lists below
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.MARKETING_MANAGER, SPACE_ROLES.EVENT_HOST_3D, SPACE_ROLES.EVENT_HOST_2D, SPACE_ROLES.DEMO_USER] },
    features: {
      'approvals-inbox':       { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING_MANAGER, SPACE_ROLES.DEMO_USER] },
      'approvals-submissions': { spaceRoles: [SPACE_ROLES.SPACE_ADMIN, SPACE_ROLES.MARKETING, SPACE_ROLES.EVENT_HOST_3D, SPACE_ROLES.EVENT_HOST_2D, SPACE_ROLES.DEMO_USER] },
    },
    // END DEMO_USER
  },

  // User analytics — global_admin sees everything; marketing_manager sees only the space they
  // have open (no filter controls). The two filter features need explicit entries because
  // a feature with no entry is open to everyone who can enter the page.
  '/user-analytics': {
    access: { systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN], spaceRoles: [SPACE_ROLES.MARKETING_MANAGER] },
    features: {
      'entity-space-filter': { systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN] },
      'email-domain-filter': { systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN] },
    },
  },

  // Platform-wide multiplayer capacity (CCU) settings — global_admin only, same shape as
  // '/user-analytics'. See MULTIPLAYER_CAPACITY_IMPLEMENTATION_PLAN.md.
  '/ccu-settings': {
    access: { systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN] },
  },

  '/admin': {
    // Explicit list — gms_accountant must NOT access the admin management portal
    access: { spaceRoles: [SPACE_ROLES.SPACE_ADMIN], systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN, SYSTEM_ROLES.ENTITY_ADMIN] },
    features: {
      // Level 1 — Entity Directory: global_admin only
      'entity-directory': { systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN] },
      'create-entity':    { systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN] },
      // Level 2 — Spaces: global_admin only
      'create-space':     { systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN] },
      // GMS Accountant invite: global_admin only
      'invite-gms-accountant': { systemRoles: [SYSTEM_ROLES.GLOBAL_ADMIN] },
    },
  },

  // Hidden — full implementation coming soon, not ready for any role yet:
  // '/subscriptions', '/ai-moderation', '/selfies', '/lighting', '/rewards'
};
